import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import useVoyagerStore from '../../stores/useVoyagerStore';
import type { LandingAgentTurn, ReadingItem, ReadingOption } from '../../stores/slices/landingAgentSlice';
import { stromaAnswerCard, stromaInset, stromaSectionLabel } from './askPresentation';

// What each line of the reading states, by its slot (the server's closed list); an unknown slot reads as itself.
const SLOT_LABELS: Record<string, string> = {
  perspective: 'Perspective', source: 'Source', target: 'Ran on', metric: 'Metric', grouping: 'Grouped by',
  filter: 'Filter', default_filter: 'Model filter', window: 'Window', ranking: 'Sorted', limit: 'Rows', opt_in: 'Draft',
  grain: 'Grain',
};
// #1174 §4.9: the cards headed by the server's own fixed sentence, each option a reading by id — the ladder's (G9, G10,
// G12) and the window offer (G4)
const LADDER_CARDS: ReadonlyArray<string> = ['rephrase', 'retry', 'restate', 'window', 'split', 'without',
  'nearest_period', 'year_or_value', 'last_month_name'];
const slotLabel = (slot: string): string => SLOT_LABELS[slot]
  || (slot.startsWith('total_') ? `Overall total · ${slotLabel(slot.slice('total_'.length))}` : slot.replace(/_/g, ' '));

// #1198 §4.5: the line naming every word the reading did not place — the server's own sentence, shown whole and first
function UnreadLine({ item }: { item: ReadingItem }) {
  return (
    <Box component="li" data-testid="reading-unread" sx={{ py: 0.75, '&:not(:last-child)': { borderBottom: '1px solid var(--line-soft)' } }}>
      <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600, overflowWrap: 'anywhere' }}>{item.text}</Typography>
    </Box>
  );
}

function ReadingLine({ item }: { item: ReadingItem }) {
  if (item.slot === 'unread') return <UnreadLine item={item} />;
  return (
    <Box component="li" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '112px 1fr' }, columnGap: 1.5, rowGap: 0.25, py: 0.75,
      '&:not(:last-child)': { borderBottom: '1px solid var(--line-soft)' } }}>
      <Typography component="span" sx={{ ...stromaSectionLabel, pt: '3px' }}>{slotLabel(item.slot)}</Typography>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ color: 'text.primary', overflowWrap: 'anywhere' }}>{item.text}</Typography>
        {item.compiled && (
          <Typography component="code" sx={{ display: 'block', mt: 0.25, fontFamily: 'var(--mono, monospace)',
            fontSize: 12, color: 'var(--ink-3)', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{item.compiled}</Typography>
        )}
      </Box>
    </Box>
  );
}

/**
 * The answer gate's reading card (#1174 P3b): how the server read the question, built by its code from what ran —
 * "Here's how I read your question:" and one line per slot — or, for a phrase the catalog knows as several metrics,
 * each metric by its catalog label and description. A click chooses by option id (`chooseReading`), once: no free
 * text, no skip. Every value is plain React text.
 */
export default function ReadingConfirmCard({ turn }: { turn: LandingAgentTurn }) {
  const chooseReading = useVoyagerStore((s) => s.chooseReading);
  const card = turn.readingConfirmation;
  if (!card) return null;
  const spent = card.chosen !== undefined;
  const decline = card.options.find((o) => o.option_id === 0);
  const offered = card.options.filter((o) => o.option_id !== 0);
  const choose = (o: ReadingOption) => { if (!spent) chooseReading(turn.id, o.option_id); };
  const chosen = spent ? card.options.find((o) => o.option_id === card.chosen) : undefined;
  // #1174 §4.9: the ladder's question in words, or Retry of a fault — the server's own fixed sentence heads the card;
  // §4.1b (G4): and the window offer's; G12: and the L3 offers' (split, without, nearest_period, year_or_value)
  const ladder = LADDER_CARDS.includes(card.kind) ? (card.reading[0]?.text ?? '') : null;
  const several = card.kind === 'several_metrics' || card.kind === 'no_exact_field'
    || card.kind === 'unstated_aggregation' || card.kind === 'average_unit' || ladder !== null;
  const field = card.kind === 'no_exact_field';
  // #1174 P5: an aggregation the words left unstated, or an average with no unit
  const aggregation = card.kind === 'unstated_aggregation' ? 'is stored row by row. The total, or each row?'
    : card.kind === 'average_unit' ? 'is an average. An average over what?' : null;

  return (
    <Paper elevation={0} data-testid={`reading-card-${card.kind}`} sx={{ p: { xs: 2, sm: 2.5 }, ...stromaAnswerCard, display: 'grid', gap: 1.5 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.35 }}>
        {ladder !== null
          ? ladder
          : aggregation
          ? (card.phrase ? `“${card.phrase}” ${aggregation}` : `This ${aggregation}`)
          : field
          ? (card.phrase ? `“${card.phrase}” has no exact match. Did you mean one of these fields?` : 'This has no exact match. Did you mean one of these fields?')
          : several
          ? (card.phrase ? `“${card.phrase}” can mean more than one metric. Which did you mean?` : 'This can mean more than one metric. Which did you mean?')
          : card.kind === 'confirm_meaning'
            ? 'This answer uses a metric no one has confirmed yet. Here is how it is defined — is that what you mean?'
            : 'Here’s how I read your question:'}
      </Typography>
      {!several && (
        <Box component="ul" data-testid="reading-lines" sx={{ ...stromaInset, listStyle: 'none', m: 0, px: 1.5, py: 0.5 }}>
          {card.reading.map((item, i) => <ReadingLine key={`${item.source}:${i}`} item={item} />)}
        </Box>
      )}
      {several && decline && (
        <Box>
          <Button variant="text" disabled={spent} onClick={() => choose(decline)} sx={{ color: 'text.secondary' }}>
            {decline.label}
          </Button>
        </Box>
      )}
      {several && (
        <Box sx={{ display: 'grid', gap: 0.75 }}>
          {offered.map((o) => (
            <Button key={o.option_id} variant="outlined" disabled={spent} onClick={() => choose(o)}
              aria-label={o.description ? `${o.label} — ${o.description}` : o.label}
              sx={{ justifyContent: 'flex-start', textAlign: 'left', textTransform: 'none', gap: 1.5, px: 1.5, py: 1.1, borderRadius: 1.5,
                borderColor: card.chosen === o.option_id ? 'var(--accent)' : 'var(--line-soft)', color: 'text.primary',
                bgcolor: card.chosen === o.option_id ? 'var(--accent-soft)' : 'transparent' }}>
              <Box component="span" sx={{ width: 22, height: 22, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontSize: 12, fontWeight: 600, bgcolor: 'var(--sunk)', color: 'var(--ink-2)' }}>{o.option_id}</Box>
              <Box component="span" sx={{ display: 'grid', minWidth: 0 }}>
                <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>{o.label}</Typography>
                {o.description && <Typography component="span" variant="caption" color="text.secondary">{o.description}</Typography>}
              </Box>
            </Button>
          ))}
        </Box>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {/* option 0 "No — don't answer" is listed first on every answer-path card (#1174 §3) */}
        {!several && decline && (
          <Button variant="outlined" disabled={spent} onClick={() => choose(decline)}>{decline.label}</Button>
        )}
        {!several && offered.map((o) => (
          <Button key={o.option_id} variant="contained" disabled={spent} onClick={() => choose(o)}>{o.label}</Button>
        ))}
        {chosen && (
          <Typography variant="caption" color="text.secondary" data-testid="reading-chosen">
            {chosen.option_id === 0 ? 'You chose not to answer.' : `You chose: ${chosen.label}`}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}
